// shared/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import _ from 'lodash';
import { ForwardedRef, useImperativeHandle, useRef, useState } from 'react';
import type { RefSelectProps } from 'antd';
import type { ICustomSelectProps, optionSelect, ValueSelect } from './types';

/**
 * Stand-in dropdown visibility handler used when the custom select receives no onDropdownVisibleChange prop. Performs no work.
 *
 * @returns Nothing.
 */
const NOOP_DROPDOWN_VISIBLE_CHANGE = () => {};

/**
 * Backs the custom select with dropdown open state, an imperative focus handle and name-aware change forwarding. Defaults the visibility callback and field name, tracks dropdownOpen locally, and exposes focusAndOpenSelectInput through useImperativeHandle so a parent can focus the input and open the dropdown.
 *
 * @param props - Field name and the change and dropdown visibility callbacks.
 * @param ref - Forwarded ref receiving the imperative focus handle.
 * @returns The dropdown state, select input ref, and the change and visibility handlers.
 */
export const useCustomSelect = (
  props: Pick<ICustomSelectProps, 'name' | 'onChange' | 'onDropdownVisibleChange'>,
  ref: ForwardedRef<unknown>,
) => {
  const { onDropdownVisibleChange = NOOP_DROPDOWN_VISIBLE_CHANGE, name = '' } = props;
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const seletInputRef = useRef<RefSelectProps>(null);

  /**
   * Forwards a selection change from the custom select to its parent. Coerces a nil value to null and calls props.onChange with the field name, resolved value and selected option.
   *
   * @param value - The newly selected value.
   * @param option - The selected option object.
   * @returns Nothing.
   */
  const onChange = (value: ValueSelect, option: optionSelect) => {
    const returnValue = _.isNil(value) ? null : value;
    props.onChange?.(name, returnValue, option);
  };

  useImperativeHandle(ref, () => ({
    focusAndOpenSelectInput: () => {
      if (seletInputRef.current) {
        seletInputRef.current.focus();
        setDropdownOpen(true);
      }
    },
  }));

  /**
   * Records the custom select's dropdown visibility and notifies the parent. Sets local dropdownOpen state and calls the onDropdownVisibleChange prop with the new value.
   *
   * @param open - Whether the dropdown is now open.
   * @returns Nothing.
   */
  const handleDropdownVisibleChange = (open: boolean) => {
    setDropdownOpen(open);
    onDropdownVisibleChange(open);
  };

  return { dropdownOpen, seletInputRef, onChange, handleDropdownVisibleChange };
};
